"use client";

import type { CampaignQuestState } from "@/components/QuestsV2/types";
import { Card, CardContent } from "@/components/ui/card";
import { List, ListItem, listItemTextVariants } from "@/components/ui/list";
import { Progress } from "@/components/ui/progress";
import { useLoginModal } from "@/context/LoginModalContext";
import { usePassportProvider } from "@/context/PassportProvider";
import { ConfirmationDialog } from "@biom3/react";
import { IterationCcw } from "lucide-react";
import { forwardRef, useState } from "react";
import {
  GemsBadge,
  PerpetualRewardsBadge,
  PointsBadge,
  WeeklyPointsBadge,
} from "./quest-badges";
import { getQuestIcon } from "./types";

// Quest Task Item Component
interface QuestTaskItemProps {
  quest: CampaignQuestState;
  disabled: boolean;
  onClick?: (quest: CampaignQuestState) => void;
  variant?: "default" | "compact";
}

const QuestTaskItem = ({
  quest,
  disabled,
  onClick,
  variant = "default",
}: QuestTaskItemProps) => {
  const QuestIcon = getQuestIcon(quest.type, quest.categories);
  const { isLoggedIn } = usePassportProvider();
  const { openLoginModal } = useLoginModal();
  const [showQuestCompletedModal, setShowQuestCompletedModal] = useState(false);
  const isCompact = variant === "compact";

  const handleQuestClick = () => {
    // Wishlist quests have their own custom flow that does the wishlist after login.
    if (quest.type === "wishlist") {
      onClick?.(quest);
      return;
    }

    if (!isLoggedIn) {
      openLoginModal();
      return;
    }

    // Check if quest is completed and not a repeatable quest
    if (quest.completed && quest.refreshPeriodHours === null) {
      setShowQuestCompletedModal(true);
      return;
    }

    onClick?.(quest);
  };

  return (
    <>
      <ListItem
        variant="transparent"
        size={isCompact ? "sm" : "md"}
        className={`px-0 ${isCompact ? "py-2" : "py-2.5"} border-b border-border`}
        disabled={quest.completedToday || disabled}
        onClick={handleQuestClick}
      >
        <div
          className={`flex w-full ${
            isCompact ? "items-center gap-2" : "items-start gap-3"
          }`}
        >
          {/* Icon and Small Screen Badges */}
          <div
            className={`${
              isCompact
                ? "flex items-center gap-2 shrink-0"
                : "flex flex-col items-center gap-2 shrink-0"
            }`}
          >
            <div
              className={`${
                isCompact ? "w-9 h-9" : "w-12 h-12"
              } rounded border border-border bg-background/20 backdrop-blur-md flex items-center justify-center shrink-0`}
            >
              <QuestIcon
                className={`${isCompact ? "w-4 h-4" : "w-5 h-5"} text-foreground`}
              />
            </div>

            {/* Badges beneath icon on small screens */}
            {!isCompact && (
              <div className="flex flex-col gap-1 items-center sm:hidden">
                <WeeklyPointsBadge
                  points={quest.weeklyPoints}
                  completed={quest.completed}
                />
                <PointsBadge
                  points={quest.campaignPoints}
                  completed={quest.completed}
                />
                <GemsBadge gems={quest.gems} completed={quest.completed} />
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className={listItemTextVariants()}>
            <div
              className={`font-suisse font-medium text-sm text-foreground ${
                isCompact
                  ? "leading-tight line-clamp-2"
                  : "leading-5 cursor-default line-clamp-2"
              }`}
            >
              {quest.name}
            </div>

            {/* Default-only description, badges and metadata */}
            {!isCompact && (
              <div className="font-suisse text-xs text-muted-foreground leading-4 cursor-default space-y-2">
                {quest.caption && (
                  <p className="text-sm text-muted-foreground leading-5">
                    {quest.caption}
                  </p>
                )}
                {/* Perpetual Rewards Indicator */}
                {quest.perpetualRewardsEligible && (
                  <PerpetualRewardsBadge
                    hasCampaignPoints={quest.campaignPoints > 0}
                  />
                )}
                <div className="flex flex-wrap gap-2.5">
                  {quest.refreshPeriodHours !== null &&
                    quest.completionCount !== undefined &&
                    quest.completionCount > 0 && (
                      <div className="flex items-center gap-1">
                        <IterationCcw className="w-3 h-3 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">
                          {quest.completionCount} completion
                          {quest.completionCount !== 1 ? "s" : ""}
                        </span>
                      </div>
                    )}
                </div>
              </div>
            )}
          </div>

          {/* Badges inline (compact) OR right column (default) */}
          {isCompact ? (
            <div className="flex items-center gap-1 shrink-0">
              <WeeklyPointsBadge
                points={quest.weeklyPoints}
                completed={quest.completed}
              />
              <PointsBadge
                points={quest.campaignPoints}
                completed={quest.completed}
              />
              <GemsBadge gems={quest.gems} completed={quest.completed} />
            </div>
          ) : (
            <div className="hidden sm:flex flex-col items-end gap-1">
              <WeeklyPointsBadge
                className="self-end"
                points={quest.weeklyPoints}
                completed={quest.completed}
              />
              <PointsBadge
                className="self-end"
                points={quest.campaignPoints}
                completed={quest.completed}
              />
              <GemsBadge
                className="self-end"
                gems={quest.gems}
                completed={quest.completed}
              />
            </div>
          )}
        </div>
      </ListItem>

      {/* Quest Completed Modal */}
      <ConfirmationDialog
        visible={showQuestCompletedModal}
        outsideClicksClose
        onCloseModal={() => setShowQuestCompletedModal(false)}
      >
        <ConfirmationDialog.Content
          sx={{
            d: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "base.borderRadius.x10",
            background: "base.color.neutral.900",
            height: "auto",
            width: "calc(100dvw - 40px)",
            maxWidth: "400px",
            p: "base.spacing.x8",
          }}
        >
          <ConfirmationDialog.Content.Title
            sx={{ textAlign: "center", mb: "base.spacing.x4" }}
          >
            Quest Completed!
          </ConfirmationDialog.Content.Title>
          <ConfirmationDialog.Content.Caption
            sx={{ textAlign: "center", mb: "base.spacing.x6" }}
          >
            This quest has already been completed and cannot be repeated.
          </ConfirmationDialog.Content.Caption>
          <ConfirmationDialog.Content.Button
            size="large"
            variant="primary"
            onClick={() => setShowQuestCompletedModal(false)}
          >
            OK
          </ConfirmationDialog.Content.Button>
        </ConfirmationDialog.Content>
      </ConfirmationDialog>
    </>
  );
};

// Main Quest Group Component
export interface QuestGroupProps {
  quests: CampaignQuestState[];
  name: string;
  description?: string;
  backgroundImage?: string;
  className?: string;
  onClick?: (quest: CampaignQuestState) => void;
  isWishlisted: boolean;
  showProgress?: boolean;
}

const QuestGroup = forwardRef<HTMLDivElement, QuestGroupProps>(
  (
    {
      quests,
      name,
      description,
      backgroundImage,
      className,
      onClick,
      isWishlisted,
      showProgress = true,
    },
    ref,
  ) => {
    // Calculate progress based on total rewards earned vs total possible gems
    const totalGemsEarned = quests.reduce((sum, quest) => {
      // For completed quests, multiply gems by completion count (for recurring quests)
      if (
        quest.completed &&
        quest.completionCount &&
        quest.completionCount > 0
      ) {
        return sum + quest.gems * quest.completionCount;
      }
      return sum;
    }, 0);

    const totalPossibleGems = quests.reduce(
      (sum, quest) => sum + quest.gems,
      0,
    );

    // Ensure progress doesn't exceed 100% for recurring quests
    const progressPercentage =
      totalPossibleGems > 0
        ? Math.min((totalGemsEarned / totalPossibleGems) * 100, 100)
        : 0;

    return (
      <Card
        ref={ref}
        variant="frosted"
        backgroundImage={backgroundImage}
        className={className}
      >
        <CardContent size="lg" className="space-y-5">
          {/* Quest Title and Description */}
          <div className="space-y-2.5">
            <h2 className="font-suisse font-semibold text-2xl text-foreground leading-8">
              {name}
            </h2>
            {description && (
              <p className="font-suisse text-base text-foreground leading-6">
                {description}
              </p>
            )}
          </div>

          {/* Progress Section */}
          {showProgress && (
            <div className="flex items-center gap-5">
              <div className="flex-1">
                <Progress value={progressPercentage} />
              </div>
              <GemsBadge
                text={`${totalGemsEarned} / ${Math.max(totalPossibleGems, totalGemsEarned)}`}
                completed={totalGemsEarned > 0}
              />
            </div>
          )}

          {/* Tasks List */}
          <div className="border-t border-border pt-5">
            <List variant="flush" size="md">
              {quests.map((quest) => (
                <QuestTaskItem
                  key={quest.id}
                  quest={quest}
                  disabled={!isWishlisted && quest.type !== "wishlist"}
                  onClick={onClick}
                />
              ))}
            </List>
          </div>
        </CardContent>
      </Card>
    );
  },
);

QuestGroup.displayName = "QuestGroup";

export { QuestGroup, QuestTaskItem };
